import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { HighlightOffRounded } from '@material-ui/icons';
import {
  DocumentTemplateType,
  ISimplifiedProject,
  ProjectArchiveSummary,
} from '../../api-client/autogenerated';
import DocumentTypeTreeView, {
  ALL_DOCUMENT_TYPES,
  FILE_MANAGER_DOCUMENT_TYPE,
} from '../custom-components/DocumentTypeTreeView';
import {
  PACKAGE_DOCUMENT_TYPES,
  withCarriedPackageDocumentTypes,
} from '../custom-components/document-type-utils';
import { CancelButton } from '../custom-components/CustomButtons';
import { getProjectArchives } from '../../models/api/project';
import ProjectArchivesList from './ProjectArchivesList';

// Packages are exported with the documents they hold rather than picked on their own, so they are
// left out of the tree and added back from the selection. Module scope keeps the reference stable.
const EXPORT_SELECTABLE_DOCUMENT_TYPES = ALL_DOCUMENT_TYPES.filter(
  (type) => !PACKAGE_DOCUMENT_TYPES.includes(type as DocumentTemplateType),
);

// The file manager rides in the tree alongside the document types but is not one of them.
const EXPORT_SELECTABLE_DOCUMENT_TYPE_COUNT = EXPORT_SELECTABLE_DOCUMENT_TYPES.filter(
  (type) => type !== FILE_MANAGER_DOCUMENT_TYPE,
).length;

export type ExportDocumentTypeSelection = {
  documentTypes: DocumentTemplateType[];
  includeDesignFiles: boolean;
  includeEmails: boolean;
};

type ExportDocumentTypesDialogProps = {
  project: ISimplifiedProject;
  open: boolean;
  closeDialog: () => void;
  onSubmit: (selection: ExportDocumentTypeSelection) => void;
};

const useStyles = makeStyles(() =>
  createStyles({
    titleContainer: {
      display: 'flex',
      flexDirection: 'row',
      height: '50px',
      padding: '8px 16px 8px 16px',
      borderBottom: '1px solid #EDECEC',
      minWidth: 500,
    },
    title: {
      marginTop: '3px',
      marginLeft: '6px',
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '22px',
      lineHeight: '30px',
      textAlign: 'center',
      color: '#0947B9',
      textTransform: 'uppercase',
      justifySelf: 'center',
    },
    rootIconButton: {
      padding: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  }),
);

/**
 * Starts a project export: the exports already taken for this project, so a finished one can be
 * picked back up, and the document types a new one should contain. Everything is selected by
 * default, which matches how exports behaved before this choice existed.
 */
export default function ExportDocumentTypesDialog(props: ExportDocumentTypesDialogProps) {
  const { project, open, closeDialog, onSubmit } = props;
  const classes = useStyles();

  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<
    (DocumentTemplateType | string)[]
  >(EXPORT_SELECTABLE_DOCUMENT_TYPES);
  const [includeEmails, setIncludeEmails] = useState(true);
  const [archives, setArchives] = useState<ProjectArchiveSummary[]>([]);
  const [areArchivesLoading, setAreArchivesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedDocumentTypes(EXPORT_SELECTABLE_DOCUMENT_TYPES);
    setIncludeEmails(true);

    let isCurrent = true;
    setAreArchivesLoading(true);
    getProjectArchives(project.id)
      .then((projectArchives) => {
        if (isCurrent) setArchives(projectArchives);
      })
      // The list is a convenience; failing to load it should not block starting an export.
      .catch(() => {
        if (isCurrent) setArchives([]);
      })
      .finally(() => {
        if (isCurrent) setAreArchivesLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [open, project.id]);

  const handleExport = () => {
    onSubmit({
      documentTypes: withCarriedPackageDocumentTypes(
        selectedDocumentTypes.filter(
          (type): type is DocumentTemplateType => type !== FILE_MANAGER_DOCUMENT_TYPE,
        ),
      ),
      includeDesignFiles: selectedDocumentTypes.includes(FILE_MANAGER_DOCUMENT_TYPE),
      includeEmails,
    });
  };

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
      <DialogTitle className={classes.titleContainer}>
        <Typography className={classes.title} align="center">
          Export Project
        </Typography>
        <IconButton
          id="export-document-types-dialog-close-button"
          style={{ right: '20px', top: '12px', position: 'absolute' }}
          onClick={closeDialog}
          classes={{ root: classes.rootIconButton }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          style={{
            fontWeight: 'bolder',
            color: 'black',
            marginTop: 8,
            marginBottom: 16,
          }}
        >
          {`Project ${project.name}`}
        </Typography>

        <Typography style={{ fontWeight: 600, marginTop: 8, marginBottom: 8 }}>
          Export Log
        </Typography>
        <ProjectArchivesList
          archives={archives}
          isLoading={areArchivesLoading}
          projectId={project.id}
          projectName={project.name}
          totalSelectableDocumentTypes={EXPORT_SELECTABLE_DOCUMENT_TYPE_COUNT}
        />

        <Typography style={{ fontWeight: 600, marginTop: 24, marginBottom: 8 }}>
          New export
        </Typography>
        <Typography style={{ marginBottom: 16 }}>
          Select the document types to include. Exports keep running after this window is closed.
        </Typography>
        <DocumentTypeTreeView
          selectedDocumentTypes={selectedDocumentTypes}
          setSelectedDocumentTypes={setSelectedDocumentTypes}
          includeEmails={includeEmails}
          setIncludeEmails={setIncludeEmails}
          hiddenDocumentTypes={PACKAGE_DOCUMENT_TYPES}
        />
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between', padding: 16 }}>
        <CancelButton onClick={closeDialog}>Cancel</CancelButton>
        <Button
          color="primary"
          variant="contained"
          onClick={handleExport}
          disabled={selectedDocumentTypes.length === 0 && !includeEmails}
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
}
